import { openDB } from 'idb';
import { DATA_VERSION, INBOX_CACHE_INVALIDATION_VERSION } from './version';

const IDB_OPEN_TIMEOUT_MS = 3000;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;
    // openDB never settles while an upgrade is blocked by another tab still
    // holding an older version open -- and every cached read awaits this
    // before touching the network, so the whole dashboard spun forever.
    // Reject after a timeout so callers fall back to the network instead.
    let timeoutId;
    let rejectOpen;
    const guard = new Promise((_, reject) => {
      rejectOpen = reject;
      timeoutId = setTimeout(
        () => reject(new Error(`IndexedDB ${dbName} open timed out`)),
        IDB_OPEN_TIMEOUT_MS
      );
    });
    const open = openDB(dbName, DATA_VERSION, {
      // Another tab is blocking our upgrade; give up rather than wait.
      blocked: () => {
        rejectOpen(new Error(`IndexedDB ${dbName} upgrade blocked`));
      },
      // A newer tab needs to upgrade: release our connection so it isn't stuck.
      blocking: () => {
        this.db?.close();
        this.db = null;
      },
      terminated: () => {
        this.db = null;
      },
      upgrade(db, oldVersion, _newVersion, transaction) {
        const shouldInvalidateInboxCache =
          oldVersion > 0 && oldVersion < INBOX_CACHE_INVALIDATION_VERSION;

        if (shouldInvalidateInboxCache) {
          transaction.objectStore('inbox').clear();
          transaction.objectStore('cache-keys').delete('inbox');
        }

        // Existing databases already carry the stores added in earlier versions,
        // and createObjectStore throws on a name that is already taken.
        const createStore = (name, options) => {
          if (db.objectStoreNames.contains(name)) return;
          db.createObjectStore(name, options);
        };

        createStore('cache-keys');
        createStore('inbox', { keyPath: 'id' });
        createStore('label', { keyPath: 'id' });
        createStore('team', { keyPath: 'id' });
        createStore('canned_response', { keyPath: 'id' });
      },
    });

    try {
      this.db = await Promise.race([open, guard]);
    } catch (error) {
      // If the open eventually succeeds after we gave up, don't leave that
      // connection holding the database and blocking the next attempt.
      open.then(db => db.close()).catch(() => {});
      this.db = null;
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
